import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Section, Eyebrow, H2, GlassCard } from "../../components/site/Primitives";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { log } from "../../lib/logger";

function Portal({ title, endpoint, render, testId }) {
  const { user, loading } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(endpoint)
      .then((r) => { if (!cancelled) setD(r.data); })
      .catch((e) => log.error("Portal load failed:", e?.message));
    return () => { cancelled = true; };
  }, [endpoint]);
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <Section className="pt-40" data-testid={testId}>
      <Eyebrow>{title}</Eyebrow>
      <H2>Welcome back.</H2>
      <div className="mt-10">{d && render(d)}</div>
    </Section>
  );
}

export function ResidentPortal() {
  return <Portal title="Resident Portal · Veteran Housing" endpoint="/portal/resident/overview" testId="resident-portal"
    render={(d) => (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <GlassCard className="lg:col-span-4 p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Your Unit</div>
          <div className="font-display text-3xl mb-3">{d.unit}</div>
          <div className="text-sm text-white/65">Lease: {d.lease.start} → {d.lease.end}</div>
          <div className="text-sm text-white/65">Rent: ${d.lease.rent}/mo</div>
          <div className="text-sm text-gold mt-3">{d.rent_status}</div>
        </GlassCard>
        <GlassCard className="lg:col-span-4 p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Case Manager</div>
          <div className="font-display text-2xl mb-2">{d.case_manager.name}</div>
          <div className="text-sm text-white/65">{d.case_manager.phone}</div>
        </GlassCard>
        <GlassCard className="lg:col-span-4 p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">Upcoming Appointments</div>
          {d.upcoming_appointments.map((a) => (
            <div key={a.id} className="text-sm border-t border-white/10 first:border-t-0 py-3">
              <div className="text-white">{a.type}</div>
              <div className="text-white/50 text-xs">{a.date} · {a.with}</div>
            </div>
          ))}
        </GlassCard>
      </div>
    )} />;
}

export function TenantPortal() {
  return <Portal title="Tenant Portal · 220 Realty" endpoint="/portal/tenant/overview" testId="tenant-portal"
    render={(d) => <TenantInner d={d} />} />;
}

function TenantInner({ d }) {
  const payRent = async () => {
    try {
      const { data } = await api.post("/payments/rent/checkout", {
        unit: d.unit, amount: d.rent_due, period: d.due_date?.slice(0, 7) || "2026-03",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <GlassCard className="lg:col-span-6 p-8">
        <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{d.unit}</div>
        <div className="font-display text-4xl mb-3">${d.rent_due.toLocaleString()}</div>
        <div className="text-sm text-white/65">Due {d.due_date} · {d.auto_pay ? "Auto-Pay ON" : "Auto-Pay OFF"}</div>
        <button onClick={payRent} className="btn-primary mt-6 text-sm" data-testid="tenant-pay-rent">
          Pay Rent Securely (Stripe) <ArrowRight size={14} />
        </button>
        <div className="text-xs text-white/40 mt-3">Test-mode Checkout · Receipts emailed automatically.</div>
      </GlassCard>
      <GlassCard className="lg:col-span-6 p-8">
        <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">Documents</div>
        {d.documents.map((doc) => (
          <div key={doc.name} className="border-t border-white/10 first:border-t-0 py-3 text-sm flex justify-between">
            <span>{doc.name}</span><span className="text-white/40">{doc.size}</span>
          </div>
        ))}
      </GlassCard>
      {d.history?.length > 0 && (
        <GlassCard className="lg:col-span-12 p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Payment History</div>
          <div className="divide-y divide-white/10">
            {d.history.map((p) => (
              <div key={p.id} className="py-3 flex items-center justify-between text-sm">
                <span>{p.unit} · {p.period}</span>
                <span className="text-white/60">{new Date(p.created_at).toLocaleDateString()}</span>
                <span className="font-display text-base">${p.amount.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
}

export function ContractorPortal() {
  return <Portal title="Contractor Portal · 220 Construction" endpoint="/portal/contractor/overview" testId="contractor-portal"
    render={(d) => (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard className="p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Open Bids</div>
          <div className="space-y-3">
            {d.open_bids.map((b) => (
              <div key={b.id} className="border border-white/10 p-4">
                <div className="font-display text-lg">{b.project}</div>
                <div className="text-xs text-white/50 mt-1">{b.trade} · Deadline {b.deadline}</div>
              </div>
            ))}
          </div>
        </GlassCard>
        <GlassCard className="p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Active Projects</div>
          <div className="space-y-4">
            {d.active_projects.map((p) => (
              <div key={p.id}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm">{p.name}</span><span className="text-xs text-gold">{p.progress}%</span>
                </div>
                <div className="h-1.5 bg-white/10"><div className="h-full bg-gold" style={{ width: `${p.progress}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-xs uppercase tracking-[0.2em] text-gold mb-3">Permits</div>
          {d.permits.map((p) => (
            <div key={p.id} className="flex justify-between border-t border-white/10 py-2 text-sm">
              <span>{p.label}</span><span className="text-white/50">{p.status}</span>
            </div>
          ))}
        </GlassCard>
      </div>
    )} />;
}
